// Live balances: a snapshot, then a stream of changes, merged by version and
// reconnected with a fresh snapshot whenever the stream ends.
import { useEffect, useRef, useState } from "react";
import { ApiError, developerApi, type ApiClient, type LiveQuery } from "./api";
import { readEventStream } from "./sse";
import type { LiveBalance } from "./types";

export interface LiveRow extends LiveBalance {
  /** The last change to the available balance seen live, for highlighting. */
  delta?: number;
  changedAt?: number;
}

export interface LiveChange {
  id: string;
  row: LiveBalance;
  delta: number;
  at: number;
  /** The account was opened after the page loaded. */
  opened?: boolean;
}

export interface LiveState {
  rows: LiveRow[];
  changes: LiveChange[];
  total: number;
}

export const EMPTY: LiveState = { rows: [], changes: [], total: 0 };
const MAX_CHANGES = 30;

/**
 * Applies one streamed balance. Stale versions are dropped; known accounts
 * update in place so the table doesn't jump; new ones go on top.
 */
export function applyChange(s: LiveState, b: LiveBalance, limit: number, now = Date.now()): LiveState {
  const i = s.rows.findIndex((r) => r.account_id === b.account_id);
  const prev = i >= 0 ? s.rows[i] : undefined;
  if (prev && b.version <= prev.version) return s;
  const delta = prev ? b.available_balance - prev.available_balance : 0;
  const row: LiveRow = { ...b, delta: prev ? delta : undefined, changedAt: now };
  let rows: LiveRow[];
  let total = s.total;
  if (prev) {
    rows = s.rows.slice();
    rows[i] = row;
  } else {
    rows = [row, ...s.rows].slice(0, limit);
    total += 1;
  }
  const changes = prev && delta === 0 && b.held_balance === prev.held_balance
    ? s.changes
    : [{ id: `${b.account_id}@${b.version}`, row: b, delta, at: now, opened: !prev && b.version === 0 }, ...s.changes].slice(0, MAX_CHANGES);
  return { rows, changes, total };
}

export type LiveStatus = "connecting" | "live" | "reconnecting" | "paused" | "locked" | "error";

const BACKOFF = [1_000, 2_000, 5_000, 10_000, 15_000];

/** Follows a game's balances while mounted and not paused. */
export function useLiveBalances(client: ApiClient, gameId: string, q: LiveQuery, paused: boolean) {
  const [state, setState] = useState<LiveState>(EMPTY);
  const [status, setStatus] = useState<LiveStatus>("connecting");
  const [error, setError] = useState<Error | null>(null);
  const key = JSON.stringify(q);
  const limit = q.limit ?? 50;
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (paused) {
      setStatus("paused");
      return;
    }
    const ac = new AbortController();
    const api = developerApi(client);
    let attempt = 0;
    const sleep = (ms: number) => new Promise<void>((res) => {
      const t = window.setTimeout(res, ms);
      ac.signal.addEventListener("abort", () => { window.clearTimeout(t); res(); }, { once: true });
    });

    (async () => {
      while (!ac.signal.aborted) {
        setStatus(attempt === 0 ? "connecting" : "reconnecting");
        try {
          const snap = await api.liveBalances(gameId, q, ac.signal);
          if (ac.signal.aborted) return;
          // Keep highlights for accounts that are still there after a reconnect.
          const before = new Map(stateRef.current.rows.map((r) => [r.account_id, r]));
          setState((s) => ({
            rows: snap.data.map((r) => {
              const p = before.get(r.account_id);
              return p && p.version === r.version ? { ...r, delta: p.delta, changedAt: p.changedAt } : r;
            }),
            changes: s.changes,
            total: snap.total,
          }));
          setError(null);
          await readEventStream(api.liveBalanceStreamUrl(gameId, { ...q, since: snap.cursor }), client.token, (e) => {
            if (e.event === "ready") {
              attempt = 0;
              setStatus("live");
            } else if (e.event === "balance") {
              const b = JSON.parse(e.data) as LiveBalance;
              setState((s) => applyChange(s, b, limit));
            }
          }, ac.signal);
        } catch (err) {
          if (ac.signal.aborted || (err as Error).name === "AbortError") return;
          if (err instanceof ApiError && (err.status === 402 || err.status === 403 || err.status === 404 || err.status === 400)) {
            setError(err);
            setStatus(err.status === 402 ? "locked" : "error");
            return;
          }
          setError(err as Error);
        }
        if (ac.signal.aborted) return;
        setStatus("reconnecting");
        await sleep(BACKOFF[Math.min(attempt++, BACKOFF.length - 1)]);
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, gameId, key, paused, limit]);

  return { ...state, status, error };
}
